/**
 * @file inward-filter-bar.tsx
 * @module app/modules/inward/components/inward-filter-bar
 * @description Loading bay manager and dock filter dropdowns for the Inward PO and GRN lists.
 *   Options come from the selected warehouse.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";

export interface InwardOption {
  _id: string;
  name: string;
}

export interface InwardFilterValue {
  loadingBayManager: string;
  dock: string;
}

interface InwardFilterBarProps {
  value: InwardFilterValue;
  onChange: (value: InwardFilterValue) => void;
}

const InwardFilterBar = ({ value, onChange }: InwardFilterBarProps) => {
  const { selectedWarehouse } = useWarehouse();
  const [managers, setManagers] = useState<InwardOption[]>([]);
  const [docks, setDocks] = useState<InwardOption[]>([]);

  useEffect(() => {
    (async () => {
      const response = await new APICallService(
        INWARD.filterOptions,
        {},
        "",
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI();
      setManagers(response?.loadingBayManagers ?? []);
      setDocks(response?.docks ?? []);
    })();
  }, [selectedWarehouse?._id]);

  return (
    <div className="inward-filter-selects">
      <Form.Select
        className="inward-input"
        aria-label="Filter by loading bay manager"
        value={value.loadingBayManager}
        onChange={(e) =>
          onChange({ ...value, loadingBayManager: e.target.value })
        }
        data-testid="filter-loading-bay-manager"
      >
        <option value="">All loading bay managers</option>
        {managers.map((m) => (
          <option key={m._id} value={m._id}>
            {m.name}
          </option>
        ))}
      </Form.Select>
      <Form.Select
        className="inward-input"
        aria-label="Filter by dock"
        value={value.dock}
        onChange={(e) => onChange({ ...value, dock: e.target.value })}
        data-testid="filter-dock"
      >
        <option value="">All docks</option>
        {docks.map((d) => (
          <option key={d._id} value={d._id}>
            {d.name}
          </option>
        ))}
      </Form.Select>
    </div>
  );
};

export default InwardFilterBar;
